import type { FC, ReactNode } from 'react';
import { Link } from 'react-router';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import { BlogLinks, MenuLink, PageLinks } from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  FOOTER_CATEGORY_LIMIT,
  categoryColumns,
  menuItems,
  type FooterProps,
} from '@/features/storefront/layouts/chrome/types';
import { STOREFRONT_MONO_STYLE } from '@/features/storefront/theme';
import { cn } from '@/lib/cn';

/**
 * The footer grid, by how many link columns stand beside the wordmark. Written
 * out for the same reason Serra's and Voltic's are: Tailwind reads these class
 * names out of the source and never sees a string built at runtime.
 */
const FOOTER_GRID: Record<number, string> = {
  0: '',
  1: 'lg:grid-cols-[1.5fr_1fr]',
  2: 'lg:grid-cols-[1.5fr_1fr_1fr]',
  3: 'lg:grid-cols-[1.5fr_1fr_1fr_1fr]',
  4: 'lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr]',
};

/**
 * A rule on an inverted ground.
 *
 * Drawn as a thin block of the page's own background colour at low opacity
 * rather than as a white border, so the line survives a merchant repainting
 * the theme: on their palette this footer's ground is whatever their text
 * colour is, and a hardcoded white hairline disappears the moment that goes
 * pale.
 */
function Rule({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('h-px', className)}
      style={{ backgroundColor: 'var(--storefront-background)', opacity: 0.16 }}
    />
  );
}

/**
 * One column: a yellow heading in capitals over its links.
 *
 * The heading wears the accent because on a black ground it is the only thing
 * that separates a column's name from its contents - Voltic can set a footer
 * heading solid and let the ink do that work, and a theme that inverted its
 * footer cannot.
 */
function Column({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div>
      <p
        className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em]"
        style={{ color: 'var(--storefront-accent)' }}
        aria-hidden={title ? undefined : true}
      >
        {title ?? ' '}
      </p>
      {children}
    </div>
  );
}

/**
 * The trade counter's back page: an inverted directory under a hazard stripe.
 *
 * The only footer in the set that turns the page over - black ground, white
 * type - which is what the rest of this theme has been building to: the
 * promise rail at the top of the shop and the fine print at the bottom of it
 * are the same colour, and everything between them is the catalogue.
 *
 * The column arithmetic is Serra's and Voltic's, because the problem is the
 * same one: the merchant's footer menu, the shop's own shelves, its pages and
 * its blogs, each skipped when empty and the grid sized to what is left - the
 * alternative being a directory of headings with nothing under them.
 */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const shelfColumns = categoryColumns(
    useShelfCategories({ limit: FOOTER_CATEGORY_LIMIT, enabled: !preview }),
  );
  const categoryPath = useCategoryPath();
  const copy = useShopContent();
  const columns = (items.length > 0 ? 1 : 0) + shelfColumns.length + (preview ? 0 : 1);
  // The merchant may well have called their own menu "Shop" already, and two
  // columns under one heading read as a single list broken in half. Compared
  // against the shop's own word for the column, in the shopper's language.
  const shopTitle = copy('theme.footer_shop');
  const shelvesTitle =
    menu?.name.trim().toLowerCase() === shopTitle.trim().toLowerCase()
      ? copy('theme.departments')
      : shopTitle;

  return (
    <footer
      className="text-[13px]"
      style={{
        backgroundColor: 'var(--storefront-text)',
        color: 'var(--storefront-background)',
      }}
    >
      {/* The hazard stripe: the theme signing the bottom of the page the same
          way the masthead signs the top of it. */}
      <div aria-hidden className="h-1" style={{ backgroundColor: 'var(--storefront-accent)' }} />

      <StorefrontContainer
        className={cn('grid gap-10 pb-12 pt-14 text-start sm:grid-cols-2', FOOTER_GRID[columns])}
      >
        <div>
          <p
            className="text-2xl font-bold uppercase tracking-[0.04em]"
            style={{ fontFamily: 'var(--storefront-heading-font)' }}
          >
            {storeName}
          </p>
          {description ? (
            <p className="mt-4 max-w-[22rem] leading-relaxed opacity-75">{description}</p>
          ) : null}
          {/* The counter's details, in the face this theme sets its numbers
              in - a trade shop is phoned as often as it is browsed. */}
          <p
            className="mt-6 text-[11px] uppercase tracking-[0.1em] opacity-60"
            style={STOREFRONT_MONO_STYLE}
          >
            {copy('theme.trade_counter_hours')}
          </p>
        </div>

        {items.length > 0 ? (
          <Column title={menu?.name ?? shopTitle}>
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-3">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="opacity-80 hover:opacity-100"
                />
              ))}
            </nav>
          </Column>
        ) : null}

        {shelfColumns.map((column, index) => (
          <Column key={column[0].id} title={index === 0 ? shelvesTitle : undefined}>
            <nav
              aria-label={
                index === 0 ? shelvesTitle : copy('theme.footer_continued', { title: shelvesTitle })
              }
              className="flex flex-col items-start gap-3"
            >
              {column.map((category) => (
                <Link
                  key={category.id}
                  to={categoryPath(category.slug)}
                  className="opacity-80 hover:opacity-100"
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </Column>
        ))}

        {preview ? null : (
          <Column title={copy('theme.footer_help')}>
            <PageLinks className="mb-3 flex-col items-start justify-start gap-3 opacity-80" />
            <BlogLinks className="flex-col items-start justify-start gap-3 opacity-80" />
            <InstallAppSlot className="mt-4" />
            <AppStoreBadges footer={footer} preview={preview} className="mt-4" />
          </Column>
        )}
      </StorefrontContainer>

      <Rule />
      <StorefrontContainer className="flex flex-wrap justify-between gap-x-6 gap-y-2 py-5 text-[11px] uppercase tracking-[0.1em] opacity-60">
        <LanguageSwitcherSlot placement="footer" preview={preview} className="normal-case tracking-normal" />
        <span style={STOREFRONT_MONO_STYLE}>{copy('theme.prices_all_inr_gst_included')}</span>
        <span style={{ ...STOREFRONT_MONO_STYLE, ...copyrightStyle(footer) }}>
          &copy; {new Date().getFullYear()} {storeName} &middot;{' '}
          {copy('theme.powered_by', { brand: 'NeonCarts' })}
        </span>
      </StorefrontContainer>
    </footer>
  );
};
